import React, { useMemo } from 'react';

import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { SearchSelect } from '@/design-system/search-select';
import type { UseDriversDropdownResult } from '@/pages/scheduled-instant/components/useDriversDropdown';

import { riderDisplayName, riderPhoneOf } from './riderLookup';

/**
 * Direct4WRiderStep — the rider block of every 4W wizard as a CONTROLLED
 * component: the directory picker (`GET /v3/drivers`, DRIVER + assignable
 * only), the editable driver name and the phone. Picking from the directory
 * reconciles name + phone from the authoritative record; the parent decides
 * where those land (the 4W modal keeps the name on `route.driverName`).
 *
 * Renders a fragment of `<Field>`s so a parent can place them inside its own
 * grid alongside plate / unit / shift — the 4W modal's "Kendaraan & driver"
 * section keeps its exact layout that way.
 */

export interface RiderValue {
  /** Directory id as a string; '' = none picked. */
  id: string;
  /** Mitra code (frozen onto the route so ops can filter / read it). */
  code: string;
  name: string;
  phone: string;
}

export interface Direct4WRiderStepProps {
  value: RiderValue;
  /** Only the keys present in the patch changed. */
  onChange: (patch: Partial<RiderValue>) => void;
  /** The parent's `useDriversDropdown(open, { type: 'DRIVER' })` result. */
  riders: UseDriversDropdownResult;
  /** Validation message for the picker (required). */
  error?: string;
  /** Unique id prefix for the `htmlFor` wiring. */
  idPrefix?: string;
  /** Extra classes on the text inputs (e.g. the greyed "dari UJP" look). */
  fieldClassName?: string;
  /** Show the free-text name / phone fields. Default true. */
  showNameAndPhone?: boolean;
}

const Direct4WRiderStep: React.FC<Direct4WRiderStepProps> = ({
  value,
  onChange,
  riders,
  error,
  idPrefix = 's4w',
  fieldClassName,
  showNameAndPhone = true,
}) => {
  const riderOptions = useMemo(
    () =>
      riders.options.map((d) => {
        const name = riderDisplayName(d);
        return {
          value: String(d.id),
          label: d.code ? `${name} · ${d.code}` : name,
        };
      }),
    [riders.options]
  );

  // Pick a rider from the directory → fill the id, and reconcile the driver
  // name + phone from the authoritative record.
  const handleSelectRider = (next: string | number | undefined) => {
    const id = next != null ? String(next) : '';
    const d = riders.options.find((o) => String(o.id) === id);
    const patch: Partial<RiderValue> = { id, code: d?.code ?? '' };
    if (d) {
      patch.name = riderDisplayName(d);
      patch.phone = riderPhoneOf(d);
    }
    onChange(patch);
  };

  return (
    <>
      {showNameAndPhone && (
        <Field>
          <Label htmlFor={`${idPrefix}-driver-name`}>Nama driver</Label>
          <InputRoot size="md" className={fieldClassName}>
            <Input
              id={`${idPrefix}-driver-name`}
              value={value.name}
              placeholder="mis. Budi Santoso"
              onChange={(e) => onChange({ name: e.target.value })}
            />
          </InputRoot>
        </Field>
      )}
      <Field>
        <Label htmlFor={`${idPrefix}-driver-id`} required>
          Driver ID
        </Label>
        <div id={`${idPrefix}-driver-id`}>
          <SearchSelect
            options={riderOptions}
            value={value.id || undefined}
            selectedLabelFallback={
              value.id
                ? value.name
                  ? `${value.name} · ${value.id}`
                  : `Driver ${value.id}`
                : undefined
            }
            onChange={handleSelectRider}
            placeholder="Cari driver (nama / kode / HP)…"
            error={!!error}
            loading={riders.loading}
            hasMore={riders.hasMore}
            disableLocalFilter
            onSearchChange={riders.setSearch}
            onLoadMore={riders.loadMore}
            emptyMessage={riders.error ?? 'Driver tidak ditemukan'}
          />
        </div>
        {error ? (
          <Hint tone="error">{error}</Hint>
        ) : (
          <Hint>
            Cari &amp; pilih driver 4W dari direktori (v3/drivers). Rider 2W
            tidak muncul di sini.
          </Hint>
        )}
      </Field>
      {showNameAndPhone && (
        <Field>
          <Label htmlFor={`${idPrefix}-driver-phone`}>No. HP driver</Label>
          <InputRoot size="md" className={fieldClassName}>
            <Input
              id={`${idPrefix}-driver-phone`}
              value={value.phone}
              placeholder="mis. 0812xxxxxxx"
              onChange={(e) => onChange({ phone: e.target.value })}
            />
          </InputRoot>
        </Field>
      )}
    </>
  );
};

export default Direct4WRiderStep;
